"use client";

import type React from "react";
import parse from "html-react-parser";
import Image from "next/image";
import { format, parseISO } from "date-fns";
import dynamic from 'next/dynamic';
import type { HeadingInfo } from "../lib/markdownToHtml";

const AnchorNavigation = dynamic(() => import('./AnchorNavigation'), {
  ssr: true
});

interface BlogPostProps {
  post: {
    title: string;
    date: string;
    content: string;
    socialImage?: string;
  };
  content: string;
  headings: HeadingInfo[];
}

const BlogPost: React.FC<BlogPostProps> = ({ post, content, headings }) => {
  const formattedDate = format(parseISO(post.date), "LLLL d, yyyy 'at' h:mm a");

  return (
    <div className="container mx-auto px-4">
      <div className="flex flex-col lg:flex-row justify-between relative">
        <article className="grow max-w-3xl">
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-4">{post.title}</h1>
            <p className="text-gray-600 dark:text-gray-400">{formattedDate}</p>
          </div>
          {post.socialImage && (
            <div className="aspect-w-2 aspect-h-1 mb-8">
              <Image
                src={post.socialImage.replace(/\.(jpg|jpeg|png|webp)$/, '-blog.webp')}
                alt={post.title}
                width={800}
                height={400}
                priority
                className="rounded-lg object-cover"
              />
            </div>
          )}
          <div className="prose dark:prose-invert prose-lg max-w-none prose-a:text-link prose-a:hover:text-link-hover">
            {parse(content)}
          </div>
        </article>
        <div className="hidden lg:block w-64 ml-8">
          <AnchorNavigation headings={headings} />
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
